import { type FC } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import { IconStatsClose, IconSwipeUp } from 'assets/svg/sprite'
import { ShowMore } from 'components/ShowMore/ShowMore'
import { ThreeDotsTrigger } from 'components-ref/atoms'
import { LastPostInfo } from 'components-ref/molecules'
import { usePreviewContext } from 'context/previewContext'
import { MinimizeIcon } from 'features/Preview/Preview'
import PreviewActions from 'features/Preview/PreviewActions/PreviewActions'
import { useOrientation } from 'helpers/helperHooks'
import { renderPostText } from 'helpers/postHelpers'
import colors from 'styles/colors'
import { type IFile } from 'types/interfaces/IFile'
import { type IPost } from 'types/interfaces/ITypes'

interface Props {
    post: IPost
    showControls: boolean
    isFirstPost: boolean
    hasNextPage: boolean
    minimizeFn: (fileIndex: number, type: string) => void
    toggleControls: () => void
    isLastPost?: boolean
    setIsOptionsNavOpen?: (val: boolean) => void
    onClose?: () => void
    onOpenOptions?: () => void
}
const SinglePostPreviewText: FC<Props> = ({
    post,
    showControls,
    hasNextPage,
    minimizeFn,
    toggleControls,
    isLastPost = false,
    setIsOptionsNavOpen,
    onOpenOptions,
    onClose
}) => {
    const deviceOrientation = useOrientation()
    const { t } = useTranslation()
    const isPortrait = deviceOrientation?.type?.includes('portrait') ?? true
    const {
        userId,
        query,
        minimized,
        setCommentsActive,
        clearModal
    } = usePreviewContext()

    const extractMedia = (post: IPost) => {
        let tmpArr: IFile[] = []

        if (post.photos) {
            tmpArr = [...post.photos]
        }
        if (post.videos) {
            tmpArr = [...tmpArr, ...post.videos]
        }
        if (post.sounds) {
            tmpArr = [...tmpArr, ...post.sounds]
        }

        return tmpArr
    }
    const postFile = {
        userId: post.user_id,
        name: post.user?.display_name,
        username: post.user?.username,
        text: post.body,
        mentions: post.tags,
        media: extractMedia(post),
        minimizedName: post.user?.display_name,
        minimizedAvatar:
            post.user?.cropped_avatar?.url ??
            post.user?.avatar?.url ??
            placeholderAvatar
    }
    if (!postFile.text) return null
    return (
        <>
            <div
            className='preview__close'
            onClick={e => {
                e.stopPropagation()

                if (onClose) {
                onClose()
                } else {
                clearModal()
                }
            }}
            >
                {minimized ? (
                    <IconStatsClose color='#A3A3A3' />
                ) : (
                    <IconStatsClose color={colors.blueDark} />
                )}
            </div>
            {showControls && (
            <ThreeDotsTrigger
                color={colors.blueDark}
                onClick={e => {
                e.stopPropagation()
                onOpenOptions?.()
                }}
            />
            )}
            <div className='preview__file--wrapper preview__file--textOnly'>
                {false && (
                <div className={classNames(minimized && 'minimized-text')}>
                    <MinimizeIcon
                    {...{ minimized, post, showControls }}
                    onClick={() => {
                        minimizeFn(0, 'text')
                    }}
                    outlineColor={
                        minimized ? colors.white : colors.blueDark
                    }
                    />
                </div>
                )}
                {showControls && !minimized && (
                    <>
                        <ShowMore
                            userId={postFile.userId}
                            name={postFile.name}
                            username={postFile.username}
                            time={post.created_at}
                            tags={['#bowieblue', '#bluechowchow']}
                            text={postFile.text ? postFile.text : ''}
                            mentions={postFile.mentions}
                        />
                        <PreviewActions
                            // userId={userId}
                            // filter={filter}
                            stats={{
                                overallRevenue: post.overall_revenue,
                                purchasesCount: post.purchases_count,
                                purchasesSum: post.purchases_sum,
                                tipsCount: post.tips_count,
                                tipsSum: post.tips_sum,
                                conversionRate: post.conversion_rate,
                                uniqueImpressions: post.unique_impressions,
                                views: post.views,
                                uniqueViews: post.unique_views,
                                price: post.price

                            }}
                            modelData={{
                                postId: post.id,
                                modelId: post.user.id,
                                avatarSrc:
                                    post.user.avatar?.url ??
                                    post.user.cropped_avatar?.url ??
                                    placeholderAvatar
                            }}
                            query={query}
                            postId={post.id}
/*                             isMyPost={userId === post.user?.id}
 */                            isLiked={post.isLiked}
                            likes={post.likes_count}
                            comments={post.comment_count}
                            setCommentsActive={setCommentsActive}
                            // setActivePost={() => setSelectedPostData(post)}
                        />
                    </>
                )}
                <div
                    onClick={e => {
                        e.stopPropagation()
                        toggleControls()
                    }}
                    className='preview__file preview__file--no-media'
                >
                    {minimized ? (
                        <div className='preview__minimized'>
                            <div className='preview__file__thumb'>
                                {/* <div className="preview__background"></div> */}
                                <div className='preview__text'>
                                    {renderPostText(
                                        postFile.text,
                                        postFile.mentions
                                    )}
                                    {/* {postFile.text} */}
                                </div>
                            </div>
                            <div className='preview__minimized__content'>
                                <div className='preview__minimized__content--top'>
                                    <div className='header__avatar'>
                                        <img
                                            src={postFile.minimizedAvatar}
                                            alt={t('placeholder')}
                                        />
                                    </div>
                                    <div className='header__name'>
                                        {postFile.minimizedName}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className='preview__background'></div>
                            <div className='preview__text'>
                                {renderPostText(
                                    postFile.text,
                                    postFile.mentions
                                )}
                                {/* {postFile.text} */}
                            </div>
{/*                             {shouldShowFirstTourGuide() && (
                                <div className='preview__swipe--icon'>
                                    <IconSwipeUp />
                                    <p>{t('swipeUpForNextPost')}</p>
                                </div>
                            )} */}
                        </>
                    )}
                    {isLastPost && !hasNextPage && <LastPostInfo />}
                </div>
            </div>
        </>
    )
}

export default SinglePostPreviewText
